// One socket to the worker that owns the park. A state machine, not a pile of callbacks:
// connecting -> authenticating -> joined -> (resuming -> joined)* -> closed.
// Tickets and resume tokens only ever travel inside frames, never in the URL.

import {
  CLOSE,
  type ClientIntent,
  decodeServerFrame,
  encode,
  MATCH_SCOPED,
  PROTOCOL_VERSION,
  type ServerFrame,
  SnapDecoder,
} from '@wahala/protocol';
import { telemetry } from '../lib/telemetry';

export type NetState = 'connecting' | 'authenticating' | 'joined' | 'resuming' | 'closed';

export interface NetOptions {
  roomId: string;
  workerUrl: string;
  ticket: string;
  onFrame(f: ServerFrame): void;
  onState(s: NetState, why?: { code: number; reason: string }): void;
  /** Get a fresh ticket from the api (resume token gone, or the worker moved the park). */
  rejoin(): Promise<{ workerUrl: string; ticket: string }>;
}

const CLIENT = 'web/0.1.0';
const PING_MS = 5_000;
/** Acks go at most this often; a burst of snapshots (back from the background) acks only the last. */
const ACK_GAP_MS = 80;
/**
 * A socket that has not opened by now is closed and tried again: a worker address behind a dropped
 * firewall rule never answers, and a phone would otherwise wait out its own connect timeout.
 */
const CONNECT_MS = 8_000;
/**
 * Tries before a phone that has never got into this park stops and says so (about half a minute),
 * rather than retrying until the park's seats expire minutes later and it closes as gone.
 */
const FIRST_JOIN_TRIES = 4;
/** Codes after which reconnecting is pointless: the player must go back to the menu. */
const FINAL = new Set<number>([
  CLOSE.NORMAL,
  CLOSE.FORBIDDEN,
  CLOSE.ROOM_FULL,
  CLOSE.ROOM_GONE,
  CLOSE.REPLACED,
  CLOSE.VERSION_MISMATCH,
  CLOSE.POLICY,
]);

/** What the park session needs from a connection (the practice park runs one in the page). */
export type NetLike = Pick<NetClient, 'state' | 'clockOffset' | 'serverNow' | 'send' | 'close' | 'wake'>;

export class NetClient {
  state: NetState = 'connecting';
  /** serverTime - localTime, from the lowest-RTT pings. */
  clockOffset = 0;
  rtt = 0;
  you: string | null = null;
  private ws: WebSocket | null = null;
  private seq = 0;
  private resumeToken: string | null = null;
  private samples: Array<{ rtt: number; offset: number }> = [];
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastPong = 0;
  private attempts = 0;
  /** Got a welcome on some socket: from then on a lost connection keeps trying as before. */
  private joinedOnce = false;
  private stopped = false;
  private ticket: string | null;
  private workerUrl: string;
  /** Turns deltas back into whole views (BE-11); the rest of the client only ever sees `snap`. */
  private snaps = new SnapDecoder();
  private ackDue: number | null = null;
  private ackAt = 0;
  private ackTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly o: NetOptions) {
    this.ticket = o.ticket;
    this.workerUrl = o.workerUrl;
    this.open();
  }

  /** Server time now, by our best clock estimate. */
  serverNow(): number {
    return Date.now() + this.clockOffset;
  }

  /** The round in play, from the park's lobby frame: frames about the round carry it (BE-12). */
  private matchId: string | null = null;

  send(intent: ClientIntent): void {
    if (this.state !== 'joined' || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const round = this.matchId && MATCH_SCOPED.has(intent.t) ? { matchId: this.matchId } : {};
    this.raw({ ...intent, ...round, v: PROTOCOL_VERSION, roomId: this.o.roomId, seq: ++this.seq });
  }

  close(): void {
    // say Leave while still joined (send() goes quiet once closed): the worker then takes the seat
    // out at once, instead of holding it for the reservation as for a dropped line
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.send({ t: 'leave' });
    this.stopped = true;
    this.setState('closed');
    this.stopPing();
    this.ws?.close(1000);
    this.ws = null;
  }

  /** The app came back from the background: check the socket now rather than at the next ping. */
  wake(): void {
    if (this.stopped) return;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) this.reconnect();
    else this.ping();
  }

  private raw(f: object): void {
    this.ws?.send(encode(f as never) as Uint8Array<ArrayBuffer>);
  }

  private setState(s: NetState, why?: { code: number; reason: string }): void {
    // a reconnect that ended in a closed park, not back in it
    if (s === 'closed' && this.state === 'resuming') telemetry.reconnect('failed');
    this.state = s;
    this.o.onState(s, why);
  }

  private open(): void {
    const ws = new WebSocket(this.workerUrl);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    let opened = false;
    const connecting = setTimeout(() => {
      if (!opened && this.ws === ws) ws.close();
    }, CONNECT_MS);
    ws.onopen = () => {
      opened = true;
      clearTimeout(connecting);
      this.seq = 0;
      // a new socket starts from a whole view
      this.snaps.reset();
      this.ackDue = null;
      if (this.ticket) {
        this.setState('authenticating');
        this.raw({
          v: PROTOCOL_VERSION,
          roomId: this.o.roomId,
          seq: ++this.seq,
          t: 'auth.hello',
          ticket: this.ticket,
          client: CLIENT,
        });
        this.ticket = null; // single use
      } else if (this.resumeToken) {
        this.setState('resuming');
        this.raw({
          v: PROTOCOL_VERSION,
          roomId: this.o.roomId,
          seq: ++this.seq,
          t: 'auth.resume',
          resumeToken: this.resumeToken,
          client: CLIENT,
        });
      } else {
        ws.close();
      }
    };
    ws.onmessage = (e) => {
      let f = decodeServerFrame(new Uint8Array(e.data as ArrayBuffer));
      if (!f) return;
      if (f.t === 'snap' || f.t === 'snapd') {
        const r = this.snaps.take(f);
        if (r.ack !== null) this.ack(r.ack);
        if (!r.view) return; // a delta on a view we no longer have: the worker sends a whole one next
        f = { t: 'snap', view: r.view };
      }
      if (f.t === 'welcome') {
        this.you = f.you;
        this.resumeToken = f.resumeToken;
        this.clockOffset = f.serverTime - Date.now();
        if (this.attempts > 0) telemetry.reconnect('ok');
        this.attempts = 0;
        this.joinedOnce = true;
        this.setState('joined');
        this.startPing();
      } else if (f.t === 'pong') {
        this.onPong(f.ct, f.st);
      } else if (f.t === 'lobby') {
        this.matchId = f.matchId ?? null;
      } else if (f.t === 'bye') {
        this.lastBye = f;
      }
      this.o.onFrame(f);
    };
    ws.onclose = (e) => {
      clearTimeout(connecting);
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopPing();
      if (this.stopped) return;
      const bye = this.lastBye;
      this.lastBye = null;
      const code = bye?.code ?? e.code;
      if (FINAL.has(code)) {
        this.stopped = true;
        this.setState('closed', { code, reason: bye?.reason ?? e.reason });
        return;
      }
      // 4401 with a rejoin hint, or 1012 (worker restarting / park moved): ask the api again
      if (bye?.redirect === 'rejoin' || code === CLOSE.RESTART || (code === CLOSE.AUTH_FAILED && !this.resumeToken)) {
        this.resumeToken = null;
      }
      // the worker's address stopped answering twice running: it may be gone, and its parks taken
      // over by another worker (ST-06). The api knows where the park is now.
      if (!opened && this.attempts >= 1) this.resumeToken = null;
      // never in the park and its worker still not answering: say so now, not when the park expires
      if (!this.joinedOnce && !opened && this.attempts >= FIRST_JOIN_TRIES) {
        this.stopped = true;
        this.setState('closed', { code: CLOSE.ROOM_GONE, reason: 'unreachable' });
        return;
      }
      this.reconnect();
    };
  }

  private lastBye: Extract<ServerFrame, { t: 'bye' }> | null = null;

  /** Tell the worker which view we hold, at most every ACK_GAP_MS (the latest wins). */
  private ack(sn: number): void {
    this.ackDue = sn;
    if (this.ackTimer) return;
    const wait = this.ackAt + ACK_GAP_MS - Date.now();
    if (wait <= 0) this.flushAck();
    else this.ackTimer = setTimeout(() => this.flushAck(), wait);
  }

  private flushAck(): void {
    this.ackTimer = null;
    if (this.ackDue === null) return;
    this.ackAt = Date.now();
    this.send({ t: 'snap.ack', sn: this.ackDue });
    this.ackDue = null;
  }

  private reconnect(): void {
    if (this.stopped) return;
    this.ws?.close();
    this.ws = null;
    this.setState('resuming');
    const delay = Math.min(8_000, 250 * 2 ** this.attempts) * (0.5 + Math.random() / 2);
    this.attempts++;
    telemetry.reconnect('tried');
    setTimeout(async () => {
      if (this.stopped) return;
      if (!this.resumeToken) {
        try {
          const t = await this.o.rejoin();
          this.ticket = t.ticket;
          this.workerUrl = t.workerUrl;
        } catch (e) {
          const code = (e as { code?: string }).code;
          if (code && code !== 'network' && code !== 'no_worker') {
            this.stopped = true;
            this.setState('closed', { code: CLOSE.ROOM_GONE, reason: code });
            return;
          }
          this.reconnect();
          return;
        }
      }
      this.open();
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    this.lastPong = Date.now();
    this.ping();
    this.pingTimer = setInterval(() => {
      // two missed pongs: assume the socket is dead
      if (Date.now() - this.lastPong > 2 * PING_MS + 1_000) {
        this.ws?.close();
        return;
      }
      this.ping();
    }, PING_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  private ping(): void {
    this.send({ t: 'ping', ct: Date.now() });
  }

  private onPong(ct: number, st: number): void {
    const now = Date.now();
    this.lastPong = now;
    const rtt = now - ct;
    telemetry.rtt(rtt);
    this.samples.push({ rtt, offset: st + rtt / 2 - now });
    if (this.samples.length > 12) this.samples.shift();
    // average the five lowest-RTT samples
    const best = [...this.samples].sort((a, b) => a.rtt - b.rtt).slice(0, 5);
    this.clockOffset = best.reduce((s, x) => s + x.offset, 0) / best.length;
    this.rtt = best[0]?.rtt ?? rtt;
  }
}
